import { useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation, type Location } from 'react-router-dom'

import { useAuthStore } from '@/store'

const useAuthHydration = () => {
    const [hydrated, setHydrated] = useState(
        () => useAuthStore.persist.hasHydrated?.() ?? false
    )

    useEffect(() => {
        const unsubHydrate = useAuthStore.persist.onHydrate?.(() => {
            setHydrated(false)
        })
        const unsubFinish = useAuthStore.persist.onFinishHydration?.(() => {
            setHydrated(true)
        })

        return () => {
            unsubHydrate?.()
            unsubFinish?.()
        }
    }, [])

    return hydrated
}

export const ProtectedRoute = () => {
    const location = useLocation()
    const isAuthenticated = useAuthStore(state => state.isAuthenticated)
    const hydrated = useAuthHydration()

    if (!hydrated) {
        return (
            <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
                Preparing workspace...
            </div>
        )
    }

    if (!isAuthenticated) {
        return (
            <Navigate to="/auth/sign-in" replace state={{ from: location }} />
        )
    }

    return <Outlet />
}

export const PublicRoute = () => {
    const location = useLocation()
    const isAuthenticated = useAuthStore(state => state.isAuthenticated)
    const hydrated = useAuthHydration()

    if (!hydrated) {
        return null
    }

    // Allow access to reset-password and create-password pages even when authenticated
    const allowedWhenAuthenticated = [
        '/auth/reset-password',
        '/auth/create-password',
    ]
    const isAllowedRoute = allowedWhenAuthenticated.includes(location.pathname)

    if (isAuthenticated && !isAllowedRoute) {
        const redirectPath =
            (location.state as { from?: Location } | null)?.from?.pathname ??
            '/'
        return <Navigate to={redirectPath} replace />
    }

    return <Outlet />
}
